/**
 * Gallery Module for TitCock.tv
 *
 * Handles:
 * - Rendering the saved videos gallery grid
 * - Opening/closing the fullscreen saved viewer
 * - Saved viewer gestures and navigation
 * - Creating cards from persisted data
 */

import {
  createElement,
  clamp,
  getViewportHeight,
  translateY,
  sleep,
} from "./utils.js";

const getTranslateY = (el) => {
  if (!el) return 0;
  const style = window.getComputedStyle(el);
  const transform = style.transform;
  if (transform === "none" || !transform) return 0;

  try {
    const matrix = new DOMMatrix(transform);
    if (!isNaN(matrix.m42)) return matrix.m42;
  } catch (e) { }

  const match = transform.match(/matrix.*\((.+)\)/);
  if (match) {
    const values = match[1].split(/,\s*/);
    if (values.length === 6) return parseFloat(values[5]);
    if (values.length === 16) return parseFloat(values[13]);
  }
  return 0;
};

import {
  elements,
  savedState,
  getVideo,
} from "./state.js";
import { getLikedSlugs, getLikedVideoData, isLiked } from "./storage.js";
import { buildVideoUrl } from "./feed.js";
import { updateHeader, updateUIVisibility } from "./ui.js";
import {
  playVideo,
  resetVideoTime,
  updateAllMuteStates,
  loadVideo,
  isActiveVideo,
  ensureVideoVisible,
  markVideoAudioChecked,
} from "./player.js";

// ==========================================================================
// Card Creation from Saved Data
// ==========================================================================

/**
 * Create a video card from persisted saved data
 * @param {Object} data - Saved video data
 * @returns {HTMLElement}
 */
export const createCardFromSavedData = (data) => {
  const card = createElement("section", "card");

  // Guess video name for URL construction if missing (lazy loading fallback)
  const guessedName =
    data.name || (data.slug ? data.slug[0].toUpperCase() + data.slug.slice(1) : "");
  const videoSrc = data.videoSrc || buildVideoUrl(guessedName);
  console.log(`[Gallery] Creating card for ${data.slug}, videoSrc:`, videoSrc);

  Object.assign(card.dataset, {
    slug: data.slug,
    name: data.name || guessedName,
    category: data.category || "",
    categoryLabel: data.categoryLabel || "",
    title: data.title || "",
    author: data.author || "",
    likes: data.likes == null ? "" : String(Number(data.likes) || 0),
    redgifsUrl: data.redgifsUrl || `https://www.redgifs.com/watch/${data.slug}`,
    thumbnail: data.thumbnail || "",
  });

  const inner = createElement("div", "inner");

  const vid = createElement("video");
  vid.playsInline = true;
  vid.muted = true;
  vid.loop = true;
  vid.preload = "metadata";
  vid.referrerPolicy = "no-referrer";
  vid.dataset.src = videoSrc;
  vid.dataset.errs = "0";

  const spinner = createElement("div", "spinner");
  const errEl = createElement("div", "err");

  const hideSpinner = () => {
    spinner.classList.add("hidden");
    setTimeout(() => spinner.classList.add("gone"), 150);
  };

  const clearError = () => {
    errEl.style.display = "none";
    errEl.textContent = "";
  };

  vid.addEventListener(
    "loadeddata",
    () => {
      hideSpinner();
      clearError();
      ensureVideoVisible(vid); // Using helper from player
      if (isActiveVideo(vid)) {
        playVideo(vid);
      }
    }
  );

  vid.addEventListener(
    "canplay",
    () => {
      ensureVideoVisible(vid);
      markVideoAudioChecked(vid);
    }
  );

  vid.addEventListener("playing", clearError);
  vid.addEventListener("playing", () => {
    setTimeout(() => markVideoAudioChecked(vid, true), 250);
  });

  vid.addEventListener("ended", () => {
    resetVideoTime(vid);
    if (isActiveVideo(vid)) {
      playVideo(vid);
    }
  });

  ["waiting", "stalled", "suspend"].forEach((event) => {
    vid.addEventListener(event, () => {
      if (isActiveVideo(vid)) {
        playVideo(vid);
      }
    });
  });

  vid.addEventListener("error", () => {
    if (!isActiveVideo(vid)) {
      vid.dataset.errs = "0";
      return;
    }

    const tries = Number(vid.dataset.errs || "0");
    if (tries < 2) {
      vid.dataset.errs = String(tries + 1);
      spinner.classList.remove("gone", "hidden");
      clearError();
      vid.pause();
      resetVideoTime(vid);
      vid.load();
      playVideo(vid);

      setTimeout(() => {
        if (vid.paused || vid.readyState < 2) {
          hideSpinner();
          errEl.style.display = "grid";
          errEl.textContent = "Error. Tap to skip.";
          card.addEventListener("click", () => scrollSaved("up"), {
            once: true,
          });
        }
      }, 1500);
    } else {
      hideSpinner();
      errEl.style.display = "grid";
      errEl.textContent = "Error. Tap to skip.";
      card.addEventListener("click", () => scrollSaved("up"), { once: true });
    }
  });

  inner.append(vid, spinner, errEl);
  card.appendChild(inner);

  return card;
};

// ==========================================================================
// Gallery Rendering
// ==========================================================================

/**
 * Render the saved videos gallery grid
 */
export const renderGallery = () => {
  const { galleryGrid, savedEmpty } = elements;
  if (!galleryGrid) return;

  const allCards = Array.from(document.querySelectorAll(".card"));
  const likedSlugs = getLikedSlugs();

  galleryGrid.innerHTML = "";

  if (likedSlugs.length === 0) {
    galleryGrid.style.display = "none";
    if (savedEmpty) savedEmpty.style.display = "block";
    return;
  }

  galleryGrid.style.display = "grid";
  if (savedEmpty) savedEmpty.style.display = "none";

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach(async (entry) => {
        if (entry.isIntersecting) {
          const img = entry.target;
          const item = img.closest(".gallery-item");
          const slug = item?.dataset?.slug;

          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute("data-src");
          }

          obs.unobserve(img);
        }
      });
    },
    { rootMargin: "300px" },
  );

  likedSlugs.forEach((slug, index) => {
    const existingCard = allCards.find((c) => c.dataset.slug === slug);
    const savedData = getLikedVideoData(slug);

    let thumbnailSrc = "";

    if (savedData) {
      thumbnailSrc = savedData.thumbnail || "";
    }

    const item = createElement("div", "gallery-item");
    item.dataset.slug = slug;
    item.dataset.index = index;

    const thumb = createElement("img", "thumb");
    thumb.alt = "";
    thumb.referrerPolicy = "no-referrer";

    // Preload first 6, lazy load rest
    if (index < 6) {
      thumb.src = thumbnailSrc;
    } else {
      thumb.dataset.src = thumbnailSrc;
      observer.observe(thumb);
    }

    const playIcon = createElement("div", "play-icon");

    item.appendChild(thumb);
    item.appendChild(playIcon);

    item.addEventListener("click", () => {
      openSavedViewer(index);
    });

    galleryGrid.appendChild(item);
  });
};

// ==========================================================================
// Saved Viewer Positioning
// ==========================================================================

/**
 * Get neighboring saved cards
 * @param {number} idx
 * @returns {HTMLElement[]}
 */
const getNeighborSavedCards = (idx) => {
  return [
    savedState.cards[idx - 1],
    savedState.cards[idx],
    savedState.cards[idx + 1],
  ].filter(Boolean);
};

/**
 * Position saved cards based on current index
 * @param {number} idx
 * @param {boolean} [noTransition=false]
 */
const positionSavedCards = (idx, noTransition = false) => {
  const cards = savedState.cards;
  const vh = getViewportHeight();

  cards.forEach((card, i) => {
    let pos;
    if (i < idx - 1) pos = -2 * vh;
    else if (i === idx - 1) pos = -vh;
    else if (i === idx) pos = 0;
    else if (i === idx + 1) pos = vh;
    else pos = 2 * vh;

    card.style.transform = translateY(pos);
    if (noTransition) card.style.transition = "none";
    card.classList.add("positioned");
  });
};

/**
 * Snap saved cards back to current position
 */
const snapBackSaved = () => {
  const cards = savedState.cards;
  const idx = savedState.currentIndex;
  const vh = getViewportHeight();
  const neighbors = getNeighborSavedCards(idx);

  neighbors.forEach((card) => {
    card.style.willChange = "transform";
    card.style.transition = "transform 260ms ease-out";
  });

  if (cards[idx - 1]) cards[idx - 1].style.transform = translateY(-vh);
  if (cards[idx]) cards[idx].style.transform = translateY(0);
  if (cards[idx + 1]) cards[idx + 1].style.transform = translateY(vh);

  setTimeout(() => {
    neighbors.forEach((card) => {
      card.style.transition = "none";
      card.style.willChange = "auto";
    });
  }, 300);
};

// ==========================================================================
// Saved Card Activation
// ==========================================================================

/**
 * Activate a saved card
 * @param {number} idx
 */
const activateSavedCard = (idx) => {
  const cards = savedState.cards;
  if (!cards.length) return;

  idx = clamp(idx, 0, cards.length - 1);
  if (idx === savedState.activeIndex) return;

  // Pause all other videos
  cards.forEach((card, i) => {
    const vid = getVideo(card);
    if (!vid) return;
    if (i !== idx) {
      vid.pause();
      resetVideoTime(vid);
      vid.muted = true;
      const err = card.querySelector(".err");
      if (err) {
        err.style.display = "none";
        err.textContent = "";
      }
    }
  });

  const card = cards[idx];
  if (!card) return;

  // Update header
  updateHeader({
    slug: card.dataset.slug || "",
    categoryLabel: card.dataset.categoryLabel || "",
    author: card.dataset.author || "",
    title: card.dataset.title || "",
    likes: card.dataset.likes,
    isLiked: isLiked(card.dataset.slug),
  });

  savedState.activeIndex = idx;
  savedState.currentIndex = idx;

  const vid = getVideo(card);
  if (vid) {
    loadVideo(vid, true);
    vid.dataset.errs = "0";
    playVideo(vid);
  }

  // Preload neighbors
  const prevCard = cards[idx - 1];
  const nextCard = cards[idx + 1];

  [prevCard, nextCard].forEach((c) => {
    if (!c) return;
    const v = getVideo(c);
    if (v) {
      loadVideo(v);
    }
  });

  updateAllMuteStates();
};

// ==========================================================================
// Saved Viewer Navigation
// ==========================================================================

/**
 * Scroll to next/previous saved card
 * @param {"up"|"down"} direction
 */
export const scrollSaved = async (direction) => {
  if (!savedState.isViewerActive) return;

  const myId = (savedState.animationId || 0) + 1;
  savedState.animationId = myId;

  const cards = savedState.cards;
  if (!cards.length) return;

  const next =
    direction === "up"
      ? savedState.currentIndex + 1
      : savedState.currentIndex - 1;

  if (next < 0 || next >= cards.length) {
    snapBackSaved();
    return;
  }

  savedState.isAnimating = true;
  savedState.currentIndex = next;
  activateSavedCard(next);

  const vh = getViewportHeight();
  const neighbors = getNeighborSavedCards(next);

  neighbors.forEach((card) => {
    card.style.willChange = "transform";
    card.style.transition = "transform 300ms cubic-bezier(0.2,0.8,0.2,1)";
  });

  const idx = next;
  if (cards[idx - 1]) cards[idx - 1].style.transform = translateY(-vh);
  if (cards[idx]) cards[idx].style.transform = translateY(0);
  if (cards[idx + 1]) cards[idx + 1].style.transform = translateY(vh);

  await sleep(300);

  if (savedState.animationId !== myId) return;

  positionSavedCards(next, true);

  neighbors.forEach((card) => {
    card.style.transition = "none";
    card.style.willChange = "auto";
  });

  savedState.isAnimating = false;
};

// ==========================================================================
// Saved Viewer Open/Close
// ==========================================================================

/**
 * Open the saved viewer at a specific index
 * @param {number} index
 */
const handleSavedPopState = () => {
  if (savedState.isViewerActive) {
    closeSavedViewer(true);
  }
};

export const openSavedViewer = (index) => {
  const likedSlugs = getLikedSlugs();

  if (!likedSlugs.length) return;

  savedState.isViewerActive = true;

  // Build cards array from liked slugs - always create fresh cards
  const cardsForViewer = [];
  likedSlugs.forEach((slug) => {
    let savedData = getLikedVideoData(slug);

    // Fallback for lazy loaded items that might not have full data in storage
    if (!savedData) {
      savedData = { slug };
    }

    const card = createCardFromSavedData(savedData);
    cardsForViewer.push(card);
  });

  if (!cardsForViewer.length) {
    savedState.isViewerActive = false;
    return;
  }

  savedState.cards = cardsForViewer;

  // Clear and populate viewer content
  const { savedViewerContent, savedViewer, galleryContainer } = elements;
  if (savedViewerContent) {
    savedViewerContent.innerHTML = "";
    const frag = document.createDocumentFragment();
    savedState.cards.forEach((card) => {
      card.style.transition = "none";
      frag.appendChild(card);
    });
    savedViewerContent.appendChild(frag);
  }

  savedState.currentIndex = clamp(index, 0, savedState.cards.length - 1);
  savedState.activeIndex = -1;

  // Show viewer
  if (savedViewer) {
    savedViewer.classList.add("active");
  }

  if (galleryContainer) galleryContainer.classList.add("hidden");

  history.pushState({ savedViewer: true }, "", "#saved-viewer");
  window.addEventListener("popstate", handleSavedPopState);

  positionSavedCards(savedState.currentIndex, true);
  activateSavedCard(savedState.currentIndex);
  updateUIVisibility();
};

/**
 * Close the saved viewer
 */
export const closeSavedViewer = (fromHistory = false) => {
  if (!savedState.isViewerActive) return;

  savedState.isViewerActive = false;
  window.removeEventListener("popstate", handleSavedPopState);

  const { savedViewer, galleryContainer } = elements;

  // Show gallery first to calculate positions
  if (galleryContainer) galleryContainer.classList.remove("hidden");

  if (savedViewer) savedViewer.classList.remove("active");

  // Pause all videos
  savedState.cards.forEach((card) => {
    const vid = getVideo(card);
    if (vid) {
      vid.pause();
      resetVideoTime(vid);
    }
  });

  savedState.activeIndex = -1;
  updateUIVisibility();

  if (!fromHistory) {
    history.back();
  }
};

// ==========================================================================
// Saved Viewer Gestures
// ==========================================================================

/**
 * Handle pointer down on saved viewer
 * @param {PointerEvent} ev
 */
export const handleSavedPointerDown = (ev) => {
  if (ev.target.closest("#savedViewerBack")) return;
  if (!savedState.isViewerActive) return;

  let startY = ev.clientY;

  if (savedState.isAnimating) {
    savedState.animationId = (savedState.animationId || 0) + 1;
    savedState.isAnimating = false;

    getNeighborSavedCards(savedState.currentIndex).forEach((c) => {
      if (c) c.style.transition = "none";
    });

    const currentCard = savedState.cards[savedState.currentIndex];
    const currentDy = getTranslateY(currentCard);
    startY = ev.clientY - currentDy;
  }

  elements.savedViewer?.setPointerCapture?.(ev.pointerId);

  savedState.gesture.active = true;
  savedState.gesture.startX = ev.clientX;
  savedState.gesture.startY = startY;
  savedState.gesture.lastY = ev.clientY;
  savedState.gesture.velocity = 0;
  savedState.gesture.lastTime = performance.now();
  savedState.gesture.directionLocked = false;

  getNeighborSavedCards(savedState.currentIndex).forEach((card) => {
    card.style.willChange = "transform";
  });
};

/**
 * Handle pointer move on saved viewer
 * @param {PointerEvent} ev
 */
export const handleSavedPointerMove = (ev) => {
  if (
    !savedState.gesture.active ||
    savedState.isAnimating ||
    !savedState.isViewerActive
  ) {
    return;
  }

  ev.preventDefault();

  const dx = ev.clientX - savedState.gesture.startX;
  const dyRaw = ev.clientY - savedState.gesture.startY;

  if (!savedState.gesture.directionLocked) {
    if (Math.abs(dx) > Math.abs(dyRaw) && Math.abs(dx) > 10) {
      savedState.gesture.active = false;
      elements.savedViewer?.releasePointerCapture?.(ev.pointerId);
      snapBackSaved();
      getNeighborSavedCards(savedState.currentIndex).forEach((card) => {
        card.style.willChange = "auto";
      });
      return;
    }
    if (Math.abs(dyRaw) > 10) {
      savedState.gesture.directionLocked = true;
    }
  }

  const now = performance.now();
  let dy = dyRaw;
  const vh = getViewportHeight();
  const cards = savedState.cards;

  // Rubber band at edges
  if (savedState.currentIndex === 0 && dy > 0) dy *= 0.28;
  if (savedState.currentIndex === cards.length - 1 && dy < 0) dy *= 0.28;

  // Calculate velocity
  const dt = Math.max(1, now - savedState.gesture.lastTime);
  savedState.gesture.velocity =
    0.88 * savedState.gesture.velocity +
    0.12 * ((ev.clientY - savedState.gesture.lastY) / dt);

  savedState.gesture.lastY = ev.clientY;
  savedState.gesture.lastTime = now;

  // Move cards
  const idx = savedState.currentIndex;
  if (cards[idx - 1]) cards[idx - 1].style.transform = translateY(dy - vh);
  if (cards[idx]) cards[idx].style.transform = translateY(dy);
  if (cards[idx + 1]) cards[idx + 1].style.transform = translateY(dy + vh);
};

/**
 * Handle pointer up on saved viewer
 * @param {PointerEvent} ev
 * @param {Function} [onDoubleTap]
 */
export const handleSavedPointerUp = (ev, onDoubleTap) => {
  if (!savedState.gesture.active) return;

  savedState.gesture.active = false;
  elements.savedViewer?.releasePointerCapture?.(ev.pointerId);

  const dy = ev.clientY - savedState.gesture.startY;
  const ady = Math.abs(dy);
  const vh = getViewportHeight() * 0.33;
  const swift = Math.abs(savedState.gesture.velocity) > 0.35;
  const minDist = 30;
  const shouldScroll = swift && ady > minDist;

  if (ady > vh || shouldScroll) {
    scrollSaved(dy < 0 ? "up" : "down");
  } else {
    snapBackSaved();
  }

  // Double tap detection
  const isTap = ady < 10 && !swift;
  const now = performance.now();

  if (
    isTap &&
    now - savedState.doubleTap.lastTime < 280 &&
    savedState.currentIndex === savedState.doubleTap.lastIndex
  ) {
    if (onDoubleTap) onDoubleTap(ev);
    savedState.doubleTap.lastTime = 0;
  } else if (isTap) {
    savedState.doubleTap.lastTime = now;
    savedState.doubleTap.lastIndex = savedState.currentIndex;
  }

  getNeighborSavedCards(savedState.currentIndex).forEach((card) => {
    card.style.willChange = "auto";
  });
};

/**
 * Handle wheel event on saved viewer
 * @param {WheelEvent} ev
 */
export const handleSavedWheel = (ev) => {
  if (
    !savedState.isViewerActive ||
    savedState.isAnimating
  ) {
    return;
  }

  const dy = ev.deltaY || 0;
  if (Math.abs(dy) < 25) return;

  ev.preventDefault();

  if (savedState.wheelBusy) return;
  savedState.wheelBusy = true;

  scrollSaved(dy > 0 ? "up" : "down");

  setTimeout(() => {
    savedState.wheelBusy = false;
  }, 360);
};

// ==========================================================================
// Keyboard Handling for Saved Viewer
// ==========================================================================

/**
 * Handle keyboard events for saved viewer
 * @param {KeyboardEvent} ev
 * @returns {boolean} - True if event was handled
 */
export const handleSavedKeyDown = (ev) => {
  if (!savedState.isViewerActive) return false;

  if (ev.key === "Escape") {
    closeSavedViewer();
    return true;
  }

  if ([" ", "ArrowDown", "PageDown"].includes(ev.key)) {
    ev.preventDefault();
    scrollSaved("up");
    return true;
  }

  if (["ArrowUp", "PageUp"].includes(ev.key)) {
    ev.preventDefault();
    scrollSaved("down");
    return true;
  }

  if (ev.key === "ArrowLeft") {
    closeSavedViewer();
    return true;
  }

  return false;
};

// ==========================================================================
// Resize Handling
// ==========================================================================

/**
 * Handle resize for saved viewer
 */
export const handleSavedResize = () => {
  if (savedState.isViewerActive) {
    positionSavedCards(savedState.currentIndex, true);
  }
};
